import { AppLayout, Flashbar, FlashbarProps, Link, SplitPanel, Tabs } from '@amzn/awsui-components-core';
import React, { useState } from 'react';
import { useFinTechContext } from 'src/components/context/FintechContextProvider';

import { buildBreadcrumbChain } from 'src/utilities/RouteConfig';
import { DFPHomePageSideNavigation } from '../../dfp-home-page/DFPHomePageSideNavigation';
import { ChartSummaryContext } from '../../generic-components/ai-summary-components/ChartSummaryContext';
import { ChartSummaryPanel } from '../../generic-components/ai-summary-components/ChartSummaryPanel';
import { StatusType } from '../sas/SasInterfaces';
import { PolicyEngineContext } from './PolicyEngineContext';
import { PolicyEngineDetailsTab } from './PolicyEngineDetailsTab';
import { PolicyEngineOverview } from './PolicyEngineOverview';
import { PolicyEngineSelectionFilters } from './PolicyEngineSelectionFilters';

export const PolicyEnginePage: React.FC = () => {
  const finOpsContext = useFinTechContext();

  const [flashbarItems, setFlashbarItems] = useState<FlashbarProps.MessageDefinition[]>([]);

  // Chart Summary State
  const [summaries, setSummaries] = useState<Array<{ title: string; content: string; summaryId: string; timestamp: number }>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSplitPanelOpen, setIsSplitPanelOpen] = useState<boolean>(false);

  const addSummary = (title: string, content: string, summaryId = '') => {
    setSummaries((prev) => [{ title, content, summaryId, timestamp: Date.now() }, ...prev]);
  };

  const clearSummaries = () => {
    setSummaries([]);
  };

  const [policyEngineGraphStatus, setPolicyEngineGraphStatus] = useState<StatusType>('loading');
  const [policyEngineGraphData, setPolicyEngineGraphData] = useState<any>();

  const [policyEngineDetailsTabStatus, setPolicyEngineDetailsTabStatus] = useState<StatusType>('loading');
  const [policyEngineDetailsTabData, setPolicyEngineDetailsTabData] = useState<any>();

  const [summaryFilters, setSummaryFilters] = useState<string>('');
  const [originalFilterOptions, setOriginalFilterOptions] = useState<string>('');

  const displayNotification = (status: FlashbarProps.Type, message: string | React.ReactNode, linkMessage?: string) => {
    function addTicketLink() {
      if (linkMessage) {
        return (
          <Link variant="primary" target="_blank" href={linkMessage} color="inverted" fontSize="body-m">
            {' '}
            submit a ticket
          </Link>
        );
      }
    }
    setFlashbarItems([
      {
        type: status,
        content: (
          <>
            {message} 'or ' {addTicketLink()}
          </>
        ),
        dismissible: true,
        dismissLabel: 'Dismiss message',
        onDismiss: () => setFlashbarItems([]),
        id: 'message_1'
      }
    ]);
  };

  return (
    <ChartSummaryContext.Provider
      value={{
        summaries,
        addSummary,
        isLoading,
        setIsLoading,
        isSplitPanelOpen,
        setIsSplitPanelOpen,
        clearSummaries
      }}
    >
      <AppLayout
        contentType="cards"
        headerSelector="#h"
        navigation={<DFPHomePageSideNavigation />}
        toolsHide={true}
        notifications={<Flashbar items={flashbarItems} />}
        splitPanel={
          <SplitPanel
            header="Chart Summary"
            i18nStrings={{
              preferencesTitle: 'Split panel preferences',
              preferencesPositionLabel: 'Position',
              preferencesPositionDescription: 'Choose the default split panel position.',
              preferencesPositionSide: 'Side',
              preferencesPositionBottom: 'Bottom',
              preferencesConfirm: 'Confirm',
              preferencesCancel: 'Cancel',
              closeButtonAriaLabel: 'Close panel',
              openButtonAriaLabel: 'Open panel',
              resizeHandleAriaLabel: 'Resize split panel'
            }}
            closeBehavior="hide"
          >
            <ChartSummaryPanel />
          </SplitPanel>
        }
        onSplitPanelToggle={({ detail }) => setIsSplitPanelOpen(detail.open)}
        splitPanelPreferences={{ position: 'side' }}
        splitPanelOpen={isSplitPanelOpen}
        splitPanelSize={400}
        maxContentWidth={Number.MAX_VALUE}
        content={
          <>
            <PolicyEngineContext.Provider
              value={{
                displayNotification,
                policyEngineGraphStatus,
                setPolicyEngineGraphStatus,
                policyEngineGraphData,
                setPolicyEngineGraphData,
                policyEngineDetailsTabStatus,
                setPolicyEngineDetailsTabStatus,
                policyEngineDetailsTabData,
                setPolicyEngineDetailsTabData,
                summaryFilters,
                setSummaryFilters,
                originalFilterOptions,
                setOriginalFilterOptions
              }}
            >
              <PolicyEngineSelectionFilters />
              <Tabs
                tabs={[
                  {
                    label: 'Overview',
                    id: 'policy-engine-OV',
                    content: <PolicyEngineOverview />
                  },
                  {
                    label: 'Details',
                    id: 'policy-engine-DT',
                    content: <PolicyEngineDetailsTab />
                  }
                ]}
              />
            </PolicyEngineContext.Provider>
          </>
        }
      />
    </ChartSummaryContext.Provider>
  );
};
